import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Plus,
    Search,
    Edit2,
    Trash2,
    Globe,
    Lock,
    Disc,
    Music2,
    CheckCircle2,
    Clock,
    DollarSign,
    LayoutGrid,
    List,
    X,
    Save,
    AlertCircle,
    Loader2,
    Image as ImageIcon,
    FileText,
    FileArchive
} from "lucide-react";
import { useMusicContent } from "../../services/admin/MusicContentServices";
import ViewMusicContentModal from "../../components/admin/ViewMusicContentModal";
import { useNotification } from "../../context/NotificationContext";
import { Eye } from "lucide-react";

export default function MusicContentPage() {
    const { showNotification } = useNotification();
    const {
        loading,
        fetchMusicCategories,
        fetchAllMusic,
        createMusicProduct,
        updateMusicProduct,
        deleteMusicProduct,
        toggleMusicStatus,
        fetchMusicProductById,
        uploadZipStream
    } = useMusicContent();

    const [music, setMusic] = useState([]);
    const [categories, setCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [viewMode, setViewMode] = useState("grid"); // grid or list
    const [statusLoading, setStatusLoading] = useState({});

    // Modal States
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [viewProductId, setViewProductId] = useState(null);
    const [editingProduct, setEditingProduct] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, product: null });
    const [formData, setFormData] = useState({
        name: "",
        category_id: "",
        price: "",
        description: "",
        status: "active",
        is_popular: false,
        image_file: null,
        audio_file: null,
        pdf_file: null,
        zip_file: null,
        track_list: []
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [audioPreview, setAudioPreview] = useState(null);
    const [pdfPreview, setPdfPreview] = useState(null);
    const [zipPreview, setZipPreview] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isPolling, setIsPolling] = useState(false);
    const [pollingProductId, setPollingProductId] = useState(null);
    const fileInputRef = useRef(null);
    const audioInputRef = useRef(null);
    const pdfInputRef = useRef(null);
    const zipInputRef = useRef(null);

    const isAlbumCategory = useCallback(() => {
        const cat = categories.find(c => c.id === formData.category_id);
        return cat && cat.name.toLowerCase() === 'album';
    }, [categories, formData.category_id]);

    const isSheetCategory = useCallback(() => {
        const cat = categories.find(c => c.id === formData.category_id);
        return cat && cat.name.toLowerCase().includes('sheet');
    }, [categories, formData.category_id]);

    const isZipCategory = useCallback(() => {
        return isAlbumCategory() || isSheetCategory();
    }, [isAlbumCategory, isSheetCategory]);

    const loadData = useCallback(async () => {
        const [musicData, catData] = await Promise.all([
            fetchAllMusic(),
            fetchMusicCategories()
        ]);
        setMusic(Array.isArray(musicData) ? musicData : []);
        setCategories(Array.isArray(catData) ? catData : []);
    }, [fetchAllMusic, fetchMusicCategories]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Navigation guard for active uploads
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isSubmitting || isPolling) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isSubmitting, isPolling]);

    // Polling logic for 'processing' status
    const pollProductStatus = useCallback(async (productId) => {
        setIsPolling(true);
        setPollingProductId(productId);

        const MAX_POLLS = 60; // 5 minutes at 5s intervals
        let pollCount = 0;

        const poll = setInterval(async () => {
            pollCount++;
            if (pollCount > MAX_POLLS) {
                clearInterval(poll);
                setIsPolling(false);
                setPollingProductId(null);
                showNotification({
                    type: "warning",
                    title: "Still Processing",
                    message: "The upload is taking longer than expected, but is likely still running in the background."
                });
                loadData();
                return;
            }

            try {
                // We use a simple fetch to check status
                const res = await fetchMusicProductById(productId);
                if (res && res.status === "active") {
                    clearInterval(poll);
                    setIsPolling(false);
                    setPollingProductId(null);
                    showNotification({
                        type: "success",
                        title: "Upload Complete",
                        message: "Product is now active with all files processed."
                    });
                    loadData();
                } else if (res && res.status === "failed") {
                    clearInterval(poll);
                    setIsPolling(false);
                    setPollingProductId(null);
                    showNotification({
                        type: "failed",
                        title: "Upload Failed",
                        message: "An error occurred during background file processing."
                    });
                    loadData();
                }
            } catch (err) {
                console.error("Polling error:", err);
            }
        }, 5000);
    }, [showNotification, loadData, fetchMusicProductById]);

    const handleOpenModal = (product = null) => {
        if (product) {
            setEditingProduct(product);
            setFormData({
                name: product.name,
                category_id: product.category_id,
                price: product.price,
                description: product.description || "",
                status: product.status,
                is_popular: product.is_popular || false,
                image_file: null,
                audio_file: null,
                pdf_file: null,
                zip_file: null,
                track_list: product.track_list || []
            });
            setImagePreview(product.image);
            setAudioPreview(product.audio);
            setPdfPreview(product.pdf);
            setZipPreview(product.zip);
        } else {
            setEditingProduct(null);
            setFormData({
                name: "",
                category_id: "",
                price: "",
                description: "",
                status: "active",
                is_popular: false,
                image_file: null,
                audio_file: null,
                pdf_file: null,
                zip_file: null,
                track_list: []
            });
            setImagePreview(null);
            setAudioPreview(null);
            setPdfPreview(null);
            setZipPreview(null);
        }
        setIsFormModalOpen(true);
    };

    const handleAddTrack = () => {
        setFormData(prev => ({
            ...prev,
            track_list: [...prev.track_list, ""]
        }));
    };

    const handleRemoveTrack = (index) => {
        setFormData(prev => ({
            ...prev,
            track_list: prev.track_list.filter((_, i) => i !== index)
        }));
    };

    const handleTrackChange = (index, value) => {
        const newList = [...formData.track_list];
        newList[index] = value;
        setFormData(prev => ({
            ...prev,
            track_list: newList
        }));
    };

    const handleCloseModal = () => {
        setIsFormModalOpen(false);
        setEditingProduct(null);
        setUploadProgress(0);
        setFormData({
            name: "",
            category_id: "",
            price: "",
            description: "",
            status: "active",
            is_popular: false,
            image_file: null,
            audio_file: null,
            pdf_file: null,
            zip_file: null,
            track_list: []
        });
        setImagePreview(null);
        setAudioPreview(null);
        setPdfPreview(null);
        setZipPreview(null);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, image_file: file }));
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleAudioChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate File Type
            const ext = file.name.split('.').pop().toLowerCase();
            if (ext !== 'mp3') {
                showNotification({ type: "failed", title: "Invalid File", message: "Only MP3 files are allowed." });
                return;
            }

            // Validate File Size (50MB)
            if (file.size > 50 * 1024 * 1024) {
                showNotification({ type: "failed", title: "File Too Large", message: "Audio file must be under 50MB." });
                return;
            }

            setFormData(prev => ({ ...prev, audio_file: file }));
            const url = URL.createObjectURL(file);
            setAudioPreview(url);
        }
    };

    const handlePdfChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const ext = file.name.split('.').pop().toLowerCase();
            if (ext !== 'pdf') {
                showNotification({ type: "failed", title: "Invalid File", message: "Only PDF files are allowed." });
                return;
            }
            if (file.size > 20 * 1024 * 1024) {
                showNotification({ type: "failed", title: "File Too Large", message: "PDF file must be under 20MB." });
                return;
            }
            setFormData(prev => ({ ...prev, pdf_file: file }));
            setPdfPreview(file.name);
        }
    };

    const handleZipChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const ext = file.name.split('.').pop().toLowerCase();
            if (ext !== 'zip') {
                showNotification({ type: "failed", title: "Invalid File", message: "Only ZIP files are allowed for albums." });
                return;
            }
            if (file.size > 10 * 1024 * 1024 * 1024) {
                showNotification({ type: "failed", title: "File Too Large", message: "ZIP file must be under 10GB." });
                return;
            }
            setFormData(prev => ({ ...prev, zip_file: file }));
            setZipPreview(file.name);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic Frontend Validation
        if (!formData.name || !formData.category_id || !formData.price || !formData.description) {
            showNotification({ type: "failed", title: "Validation Error", message: "All fields except 'Popular' are required." });
            return;
        }

        if (!editingProduct && !formData.image_file) {
            showNotification({ type: "failed", title: "Validation Error", message: "Please upload a product cover image." });
            return;
        }

        // Require at least one of media files for new products
        if (!editingProduct) {
            if (isZipCategory()) {
                if (!formData.zip_file && !formData.pdf_file) {
                    const msg = isAlbumCategory() ? "Please upload either a ZIP archive or a PDF sheet for albums." : "Please upload either a ZIP archive or a PDF sheet for sheet music.";
                    showNotification({ type: "failed", title: "Validation Error", message: msg });
                    return;
                }
            } else {
                if (!formData.audio_file && !formData.pdf_file) {
                    showNotification({ type: "failed", title: "Validation Error", message: "Please upload either an audio file or a PDF sheet." });
                    return;
                }
            }
        }

        // Progress handler for Stage 2 ZIP stream
        const onZipProgress = (event) => {
            if (event.total) {
                const pct = Math.round((event.loaded * 100) / event.total);
                setUploadProgress(pct);
            }
        };

        setIsSubmitting(true);
        setUploadProgress(0);
        try {
            let res;
            if (editingProduct) {
                // Stage 1: Update metadata (ZIP is omitted in Service)
                res = await updateMusicProduct(editingProduct.id, formData);
            } else {
                // Stage 1: Create metadata (ZIP is omitted in Service)
                res = await createMusicProduct(formData);
            }

            if (res.status === "success") {
                // Stage 2: Upload ZIP if required
                if (res.will_upload_zip && formData.zip_file) {
                    setUploadProgress(0);
                    const zipRes = await uploadZipStream(res.id, formData.zip_file, onZipProgress);

                    if (zipRes.status === "failed") {
                        setIsSubmitting(false);
                        return;
                    }
                }

                if (res.product_status === "processing" || (res.will_upload_zip && formData.zip_file)) {
                    handleCloseModal();
                    pollProductStatus(res.id);
                } else {
                    handleCloseModal();
                    loadData();
                }
            }
        } catch (err) {
            console.error("Submission error:", err);
        } finally {
            setIsSubmitting(false);
            setUploadProgress(0);
        }
    };

    const handleToggleStatus = async (id) => {
        setStatusLoading(prev => ({ ...prev, [id]: true }));
        const res = await toggleMusicStatus(id);
        if (res.status === "success") {
            await loadData();
        }
        setStatusLoading(prev => ({ ...prev, [id]: false }));
    };

    const handleDeleteClick = (product) => {
        setDeleteModal({ isOpen: true, product });
    };

    const handleViewClick = (id) => {
        setViewProductId(id);
        setIsViewModalOpen(true);
    };

    const confirmDelete = async () => {
        const res = await deleteMusicProduct(deleteModal.product.id);
        if (res.status === "success") {
            setDeleteModal({ isOpen: false, product: null });
            loadData();
        }
    };

    const filteredMusic = (Array.isArray(music) ? music : []).filter(item => {
        const nameMatch = (item.name || '').toLowerCase().includes((searchTerm || '').toLowerCase());
        const catMatch = (item.category_name || '').toLowerCase().includes((searchTerm || '').toLowerCase());
        return nameMatch || catMatch;
    });

    return (
        <>
            <div className="p-6 lg:p-10 max-w-7xl mx-auto">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-normal text-black dark:text-white" style={{ fontFamily: "Playfair Display, serif" }}>
                            Music Content
                        </h1>
                        <p className="text-black dark:text-white/50 text-sm mt-1">
                            Manage your dynamic music products and digital releases.
                        </p>
                    </div>
                    <button
                        onClick={() => handleOpenModal()}
                        className="flex items-center justify-center gap-2 bg-black text-white px-6 py-3 rounded-xl text-sm font-bold tracking-widest hover:bg-jazz-brown transition-all w-full md:w-auto shadow-lg shadow-black/10"
                    >
                        <Plus size={18} /> ADD MUSIC PRODUCT
                    </button>
                </div>

                {/* Filters & Search */}
                <div className="bg-white dark:bg-[#1A1A1A] p-4 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm mb-6 flex flex-col md:flex-row gap-4 items-center">
                    <div className="relative flex-1 w-full">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-black/60 dark:text-white/40" size={18} />
                        <input
                            type="text"
                            placeholder="Search music products..."
                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm text-black dark:text-white placeholder-black/70 dark:placeholder-white/40"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="flex items-center gap-2 p-1 bg-gray-100 dark:bg-[#222] rounded-xl border border-black/5">
                        <button
                            onClick={() => setViewMode("grid")}
                            className={`p-2 rounded-lg transition-all ${viewMode === "grid" ? "bg-white dark:bg-zinc-800 shadow-sm text-jazz-brown" : "text-black dark:text-white/40"}`}
                            title="Grid View"
                        >
                            <LayoutGrid size={18} />
                        </button>
                        <button
                            onClick={() => setViewMode("list")}
                            className={`p-2 rounded-lg transition-all ${viewMode === "list" ? "bg-white dark:bg-zinc-800 shadow-sm text-jazz-brown" : "text-black dark:text-white/40"}`}
                            title="List View"
                        >
                            <List size={18} />
                        </button>
                    </div>
                </div>

                {/* Music Grid/List Display */}
                {loading && music.length === 0 ? (
                    <div className="py-40 flex flex-col items-center justify-center gap-4">
                        <Loader2 size={48} className="animate-spin text-jazz-brown" />
                        <p className="text-black dark:text-white/50 font-medium italic">Loading your music library...</p>
                    </div>
                ) : viewMode === "grid" ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {filteredMusic.map((item) => (
                            <div key={item.id} className="bg-white dark:bg-[#1A1A1A] rounded-2xl border border-black/5 dark:border-white/5 shadow-sm overflow-hidden group hover:shadow-xl hover:shadow-black/5 transition-all relative">
                                {isPolling && pollingProductId === item.id && (
                                    <div className="absolute inset-0 z-20 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center p-6 text-center">
                                        <Loader2 size={32} className="animate-spin text-white mb-3" />
                                        <p className="text-white text-xs font-bold uppercase tracking-widest">Finalizing Upload...</p>
                                        <p className="text-white/60 text-[10px] mt-2">Processing background tasks. Do not close the window.</p>
                                    </div>
                                )}
                                <div className="aspect-square relative overflow-hidden bg-gray-100 dark:bg-zinc-800">
                                    <div className="absolute top-3 right-3 z-10 flex flex-col gap-2 items-end">
                                        <span className={`px-2.5 py-1 rounded-full text-[8px] font-bold uppercase tracking-widest flex items-center gap-1 shadow-sm ${item.status === 'active' ? "bg-green-500 text-white" : item.status === 'processing' ? "bg-jazz-brown text-white" : "bg-black text-white"}`}>
                                            {item.status === 'active' ? <Globe size={10} /> : item.status === 'processing' ? <Loader2 size={10} className="animate-spin" /> : <Lock size={10} />}
                                            {item.status}
                                        </span>
                                        {item.is_popular && (
                                            <span className="px-2.5 py-1 rounded-full text-[8px] font-bold uppercase tracking-widest bg-jazz-brown text-white shadow-sm flex items-center gap-1">
                                                <CheckCircle2 size={10} /> Popular
                                            </span>
                                        )}
                                    </div>
                                    <div className="w-full h-full flex items-center justify-center">
                                        {item.image ? (
                                            <img src={item.image} alt={item.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                        ) : (
                                            <Music2 size={48} className="text-black/5" />
                                        )}
                                    </div>
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                        <button
                                            onClick={() => handleViewClick(item.id)}
                                            className="p-3 bg-white rounded-full text-black hover:bg-jazz-brown hover:text-white transition-all transform -translate-y-4 group-hover:translate-y-0 duration-300"
                                        >
                                            <Eye size={18} />
                                        </button>
                                        <button
                                            onClick={() => handleOpenModal(item)}
                                            className="p-3 bg-white rounded-full text-black hover:bg-jazz-brown hover:text-white transition-all transform -translate-y-4 group-hover:translate-y-0 duration-300 delay-75"
                                        >
                                            <Edit2 size={18} />
                                        </button>
                                        <button
                                            onClick={() => handleToggleStatus(item.id)}
                                            disabled={statusLoading[item.id]}
                                            className="p-3 bg-white rounded-full text-black hover:bg-jazz-brown hover:text-white transition-all transform -translate-y-4 group-hover:translate-y-0 duration-300 delay-100"
                                        >
                                            {statusLoading[item.id] ? <Loader2 size={18} className="animate-spin" /> : item.status === 'active' ? <Lock size={18} /> : <Globe size={18} />}
                                        </button>
                                        <button
                                            onClick={() => handleDeleteClick(item)}
                                            className="p-3 bg-white rounded-full text-red-500 hover:bg-red-500 hover:text-white transition-all transform -translate-y-4 group-hover:translate-y-0 duration-300 delay-150"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                </div>
                                <div className="p-5">
                                    <div className="flex items-center gap-2 text-xs font-bold text-jazz-brown uppercase tracking-widest mb-2">
                                        <Disc size={12} /> {item.category_name}
                                    </div>
                                    <h3 className="font-bold text-black dark:text-white mb-4 line-clamp-1 h-6 leading-6 text-lg" style={{ fontFamily: "Playfair Display, serif" }}>
                                        {item.name}
                                    </h3>
                                    <div className="flex items-center justify-between pt-4 border-t border-black/5 dark:border-white/5">
                                        <div className="flex flex-col">
                                            <span className="text-xs text-black dark:text-white/40 font-bold uppercase tracking-widest">Price</span>
                                            <span className="text-lg font-bold text-black dark:text-white flex items-center gap-0.5">
                                                <DollarSign size={14} className="text-jazz-brown" />{parseFloat(item.price).toFixed(2)}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 dark:bg-white/5 rounded-lg border border-black/5 min-w-0">
                                            <span className="text-[10px] font-bold text-black dark:text-white/50 uppercase tracking-wider truncate">Digital/Physical</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl border border-black/5 dark:border-white/5 overflow-hidden shadow-sm">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 dark:bg-[#222] border-b border-black/5">
                                <tr>
                                    <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Product</th>
                                    <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Category</th>
                                    <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Price</th>
                                    <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Status</th>
                                    <th className="px-6 py-4 text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-black/5">
                                {filteredMusic.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center shrink-0">
                                                    {item.image ? <img src={item.image} alt="" className="w-full h-full object-cover" /> : <Music2 size={20} className="text-black/10" />}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-sm text-black dark:text-white line-clamp-1">{item.name}</p>
                                                    <p className="text-xs text-jazz-brown font-bold uppercase tracking-tighter">ID: {item.id.split('-')[0]}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-black dark:text-white/60">{item.category_name}</td>
                                        <td className="px-6 py-4 font-bold text-sm text-black dark:text-white">${parseFloat(item.price).toFixed(2)}</td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${item.status === 'active' ? "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400" : "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400"}`}>
                                                {item.status === 'active' ? <CheckCircle2 size={12} /> : <Clock size={12} />} {item.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end items-center gap-2">
                                                <button onClick={() => handleViewClick(item.id)} className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-lg text-black dark:text-white/40 hover:text-black dark:hover:text-white transition-colors"><Eye size={16} /></button>
                                                <button onClick={() => handleOpenModal(item)} className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-lg text-black dark:text-white/40 hover:text-black dark:hover:text-white transition-colors"><Edit2 size={16} /></button>
                                                <button
                                                    onClick={() => handleToggleStatus(item.id)}
                                                    disabled={statusLoading[item.id]}
                                                    className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-lg text-black dark:text-white/40 hover:text-black dark:hover:text-white transition-colors"
                                                >
                                                    {statusLoading[item.id] ? <Loader2 size={16} className="animate-spin" /> : item.status === 'active' ? <Lock size={16} /> : <Globe size={16} />}
                                                </button>
                                                <button onClick={() => handleDeleteClick(item)} className="p-2 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg text-red-400 hover:text-red-600 transition-colors"><Trash2 size={16} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {!loading && filteredMusic.length === 0 && (
                    <div className="py-20 text-center">
                        <Music2 size={48} className="text-black/5 mx-auto mb-4" />
                        <p className="text-black dark:text-white/50 font-medium italic">No music products found matching your search.</p>
                    </div>
                )}
            </div>

            {/* Add/Edit Product Modal */}
            {isFormModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-hidden">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleCloseModal} />
                    <div className="relative bg-white dark:bg-[#1A1A1A] w-full max-w-3xl rounded-3xl shadow-2xl border border-black/5 dark:border-white/5 overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="px-8 py-6 border-b border-black/5 flex items-center justify-between shrink-0">
                            <div>
                                <h2 className="text-2xl font-normal text-black dark:text-white" style={{ fontFamily: "Playfair Display, serif" }}>
                                    {editingProduct ? "Edit Music Product" : "Add Music Product"}
                                </h2>
                                <p className="text-sm text-black dark:text-white/50 mt-1 uppercase tracking-widest font-bold">Product Details</p>
                            </div>
                            <button onClick={handleCloseModal} className="text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white p-2 bg-gray-100 dark:bg-zinc-800 rounded-full transition-all"><X size={20} /></button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-8 overflow-y-auto custom-scrollbar space-y-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest mb-2">
                                            Product Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.name}
                                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                            className="w-full px-5 py-4 rounded-2xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium transition-all"
                                            placeholder="Enter product title..."
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest mb-2">
                                            Category Selection <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            required
                                            value={formData.category_id}
                                            onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
                                            className="w-full px-5 py-4 rounded-2xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium transition-all appearance-none"
                                        >
                                            <option value="" disabled>Select Category</option>
                                            {categories.map(cat => (
                                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                                            ))}
                                            {categories.length === 0 && <option value="" disabled>No Music Categories Found</option>}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest mb-2">
                                            Price ($) <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-jazz-brown" size={18} />
                                            <input
                                                type="number"
                                                step="0.01"
                                                required
                                                value={formData.price}
                                                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                                                className="w-full pl-12 pr-5 py-4 rounded-2xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium transition-all"
                                                placeholder="0.00"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest mb-2">
                                            Upload Cover Image <span className="text-red-500">*</span>
                                        </label>
                                        <div
                                            onClick={() => fileInputRef.current?.click()}
                                            className="relative w-full aspect-video rounded-2xl border-2 border-dashed border-black/10 dark:border-white/10 hover:border-jazz-brown dark:hover:border-jazz-brown group cursor-pointer overflow-hidden bg-gray-50 dark:bg-[#222] transition-all flex flex-col items-center justify-center gap-2"
                                        >
                                            {imagePreview ? (
                                                <>
                                                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover transition-transform group-hover:scale-110" />
                                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                        <Plus className="text-white" size={32} />
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="p-4 bg-white dark:bg-zinc-800 rounded-full shadow-sm text-black/20 group-hover:text-jazz-brown transition-colors">
                                                        <ImageIcon size={24} />
                                                    </div>
                                                    <p className="text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Click to upload JPG/PNG</p>
                                                </>
                                            )}
                                        </div>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            className="hidden"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                        />
                                    </div>

                                    <div className="flex items-center gap-6 pt-4">
                                        <div className="flex items-center gap-3">
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    className="sr-only peer"
                                                    checked={formData.is_popular}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, is_popular: e.target.checked }))}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-jazz-brown"></div>
                                                <span className="ml-3 text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest">Mark as Popular</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Media Upload Section */}
                            <div className="bg-gray-50/50 dark:bg-white/5 p-6 rounded-2xl border border-black/5 dark:border-white/5">
                                <h3 className="text-sm font-bold text-black dark:text-white mb-1 flex items-center gap-2">
                                    <Music2 size={16} className="text-jazz-brown" /> Media Files
                                </h3>
                                <p className="text-xs text-black dark:text-white/40 italic mb-6">
                                    {isZipCategory()
                                        ? `Upload the ${isAlbumCategory() ? 'album' : 'sheet music'} ZIP archive and optionally a PDF sheet.`
                                        : "Upload at least one of either an audio (MP3) file or a PDF sheet music file."
                                    }
                                </p>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Conditional Audio or ZIP Upload */}
                                    {!isZipCategory() ? (
                                        <div>
                                            <label className="block text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest mb-2">
                                                Upload Audio (MP3)
                                            </label>
                                            <div
                                                onClick={() => audioInputRef.current?.click()}
                                                className={`relative w-full p-6 rounded-2xl border-2 border-dashed ${audioPreview ? 'border-jazz-brown bg-jazz-brown/5' : 'border-black/10 dark:border-white/10'} hover:border-jazz-brown transition-all cursor-pointer text-center group`}
                                            >
                                                <div className="flex flex-col items-center gap-3">
                                                    <div className={`p-4 rounded-full ${audioPreview ? 'bg-jazz-brown text-white' : 'bg-gray-100 dark:bg-zinc-800 text-black/20 group-hover:text-jazz-brown'} transition-colors`}>
                                                        <Music2 size={24} />
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest">
                                                            {formData.audio_file ? formData.audio_file.name : audioPreview ? "Track Selected" : "Click to upload MP3"}
                                                        </p>
                                                        <p className="text-xs text-black dark:text-white/40 mt-1 uppercase tracking-widest font-bold">Max size: 50MB</p>
                                                    </div>

                                                    {audioPreview && (
                                                        <div className="mt-4 w-full" onClick={(e) => e.stopPropagation()}>
                                                            <audio
                                                                src={audioPreview}
                                                                controls
                                                                className="w-full h-10 accent-jazz-brown"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <input
                                                type="file"
                                                ref={audioInputRef}
                                                className="hidden"
                                                accept="audio/mpeg"
                                                onChange={handleAudioChange}
                                            />
                                        </div>
                                    ) : (
                                        <div>
                                            <label className="block text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest mb-2">
                                                Upload ZIP Folder ({isAlbumCategory() ? 'Album' : 'Sheet'})
                                            </label>
                                            <div
                                                onClick={() => !isSubmitting && zipInputRef.current?.click()}
                                                className={`relative w-full p-6 rounded-2xl border-2 border-dashed ${zipPreview ? 'border-jazz-brown bg-jazz-brown/5' : 'border-black/10 dark:border-white/10'} ${isSubmitting ? 'cursor-not-allowed opacity-80' : 'hover:border-jazz-brown cursor-pointer'} transition-all text-center group overflow-hidden`}
                                            >
                                                {/* Upload progress bar — visible while ZIP is being sent */}
                                                {isSubmitting && formData.zip_file && (
                                                    <div className="absolute inset-x-0 bottom-0 h-1.5 bg-black/10 dark:bg-white/10">
                                                        <div
                                                            className="h-full bg-jazz-brown transition-all duration-300 ease-out"
                                                            style={{ width: `${uploadProgress}%` }}
                                                        />
                                                    </div>
                                                )}
                                                <div className="flex flex-col items-center gap-3">
                                                    <div className={`p-4 rounded-full ${zipPreview ? 'bg-jazz-brown text-white' : 'bg-gray-100 dark:bg-zinc-800 text-black/20 group-hover:text-jazz-brown'} transition-colors`}>
                                                        {isSubmitting && formData.zip_file
                                                            ? <Loader2 size={24} className="animate-spin" />
                                                            : <FileArchive size={24} />}
                                                    </div>
                                                    <div>
                                                        {isSubmitting && formData.zip_file ? (
                                                            <>
                                                                <p className="text-xs font-bold text-jazz-brown uppercase tracking-widest">
                                                                    {uploadProgress < 100 ? `Uploading\u2026 ${uploadProgress}%` : 'Processing on server\u2026'}
                                                                </p>
                                                                <p className="text-xs text-black dark:text-white/40 mt-1 font-medium">
                                                                    {formData.zip_file.name}
                                                                    {' '}({(formData.zip_file.size / (1024 * 1024 * 1024)).toFixed(2)} GB)
                                                                </p>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <p className="text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest">
                                                                    {formData.zip_file
                                                                        ? `${formData.zip_file.name} (${(formData.zip_file.size / (1024 * 1024 * 1024)).toFixed(2)} GB)`
                                                                        : zipPreview
                                                                            ? (typeof zipPreview === 'string' ? 'ZIP Selected' : 'Archive Selected')
                                                                            : `Click to upload ${isAlbumCategory() ? 'Album' : 'Sheet'} ZIP`}
                                                                </p>
                                                                <p className="text-xs text-black dark:text-white/40 mt-1 uppercase tracking-widest font-bold">Max size: 10GB</p>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <input
                                                type="file"
                                                ref={zipInputRef}
                                                className="hidden"
                                                accept=".zip,application/zip,application/x-zip-compressed"
                                                onChange={handleZipChange}
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    )}

                                    {/* PDF Upload */}
                                    <div>
                                        <label className="block text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest mb-2">
                                            Upload PDF Sheet
                                        </label>
                                        <div
                                            onClick={() => pdfInputRef.current?.click()}
                                            className={`relative w-full p-6 rounded-2xl border-2 border-dashed ${pdfPreview ? 'border-jazz-brown bg-jazz-brown/5' : 'border-black/10 dark:border-white/10'} hover:border-jazz-brown transition-all cursor-pointer text-center group`}
                                        >
                                            <div className="flex flex-col items-center gap-3">
                                                <div className={`p-4 rounded-full ${pdfPreview ? 'bg-jazz-brown text-white' : 'bg-gray-100 dark:bg-zinc-800 text-black/20 group-hover:text-jazz-brown'} transition-colors`}>
                                                    <FileText size={24} />
                                                </div>
                                                <div>
                                                    <p className="text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest">
                                                        {formData.pdf_file ? formData.pdf_file.name : pdfPreview ? "PDF Sheet Selected" : "Click to upload PDF"}
                                                    </p>
                                                    <p className="text-xs text-black dark:text-white/40 mt-1 uppercase tracking-widest font-bold">Max size: 20MB</p>
                                                </div>
                                            </div>
                                        </div>
                                        <input
                                            type="file"
                                            ref={pdfInputRef}
                                            className="hidden"
                                            accept="application/pdf"
                                            onChange={handlePdfChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest mb-2">Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                    className="w-full px-5 py-4 rounded-2xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium transition-all min-h-[120px] resize-none"
                                    placeholder="Enter details about this music release..."
                                />
                            </div>

                            {isAlbumCategory() && (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <label className="block text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest">
                                            Track List
                                        </label>
                                        <button
                                            type="button"
                                            onClick={handleAddTrack}
                                            className="flex items-center gap-2 text-xs font-bold text-jazz-brown hover:text-jazz-brown/80 transition-colors uppercase tracking-widest"
                                        >
                                            <Plus size={14} /> Add Track
                                        </button>
                                    </div>
                                    <div className="space-y-3">
                                        {formData.track_list.map((track, index) => (
                                            <div key={index} className="flex gap-3 group">
                                                <div className="flex-1 relative">
                                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-black/20 text-[10px] font-bold w-5 h-5 flex items-center justify-center border border-black/10 rounded-full">
                                                        {index + 1}
                                                    </div>
                                                    <input
                                                        type="text"
                                                        value={track}
                                                        onChange={(e) => handleTrackChange(index, e.target.value)}
                                                        className="w-full pl-12 pr-5 py-3 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium transition-all"
                                                        placeholder={`Track ${index + 1} title...`}
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveTrack(index)}
                                                    className="p-3 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-xl transition-all opacity-40 group-hover:opacity-100"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        ))}
                                        {formData.track_list.length === 0 && (
                                            <div className="text-center py-8 border-2 border-dashed border-black/5 rounded-2xl">
                                                <p className="text-xs text-black/30 font-bold uppercase tracking-widest">No tracks added yet</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading || isSubmitting}
                                className="w-full bg-black text-white py-5 rounded-2xl text-xs font-bold tracking-[0.2em] hover:bg-jazz-brown transition-all shadow-xl shadow-black/10 flex items-center justify-center gap-3 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading || isSubmitting ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                                {editingProduct ? "UPDATE MUSIC PRODUCT" : "SAVE MUSIC PRODUCT"}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteModal.isOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDeleteModal({ isOpen: false, product: null })} />
                    <div className="relative bg-white dark:bg-[#1A1A1A] w-full max-w-md rounded-3xl shadow-2xl border border-black/5 overflow-hidden">
                        <div className="p-8 text-center">
                            <div className="w-20 h-20 bg-red-50 dark:bg-red-900/10 rounded-full flex items-center justify-center mx-auto mb-6 text-red-500">
                                <AlertCircle size={40} />
                            </div>
                            <h3 className="text-2xl font-normal text-black dark:text-white mb-3" style={{ fontFamily: "Playfair Display, serif" }}>Confirm Deletion</h3>
                            <p className="text-black dark:text-white/50 text-sm mb-8 leading-relaxed">
                                Are you sure you want to delete <span className="font-bold text-black">"{deleteModal.product.name}"</span>?
                                <br /><br />
                                <span className="text-red-500 font-bold uppercase tracking-wider text-xs">Warning:</span> This action is permanent and cannot be undone.
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                                <button
                                    onClick={() => setDeleteModal({ isOpen: false, product: null })}
                                    className="py-4 rounded-2xl border border-black/5 text-black dark:text-white/50 text-xs font-bold tracking-widest hover:bg-gray-100 dark:hover:bg-zinc-800 transition-all"
                                >
                                    CANCEL
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    disabled={loading}
                                    className="bg-red-500 text-white py-4 rounded-2xl text-xs font-bold tracking-widest hover:bg-red-600 transition-all shadow-lg flex items-center justify-center gap-2"
                                >
                                    {loading ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />} DELETE
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* View Product Modal */}
            <ViewMusicContentModal
                itemId={viewProductId}
                isOpen={isViewModalOpen}
                onClose={() => {
                    setIsViewModalOpen(false);
                    setViewProductId(null);
                }}
            />
        </>
    );
}
